// Calculate current cycle phase based on last period date
export const calculateCurrentPhase = (lastPeriodDate, cycleLength = 28) => {
  const today = new Date()
  const lastPeriod = new Date(lastPeriodDate)
  const daysSinceLastPeriod = Math.floor((today - lastPeriod) / (1000 * 60 * 60 * 24))

  // Handle negative days (future date) or very old dates
  if (daysSinceLastPeriod < 0 || daysSinceLastPeriod > cycleLength * 2) {
    return null
  }

  const cycleDay = (daysSinceLastPeriod % cycleLength) + 1

  // Determine menstrual phase
  let menstrualPhase, moonPhase, phaseName, description

  if (cycleDay >= 1 && cycleDay <= 5) {
    menstrualPhase = 'Menstrual'
    moonPhase = 'New Moon'
    phaseName = 'Deep Renewal'
    description = 'Your body is releasing and renewing. This is your time for rest, reflection, and deep visioning. Your intuition is heightened.'
  } else if (cycleDay >= 6 && cycleDay <= 13) {
    menstrualPhase = 'Follicular'
    moonPhase = 'Waxing Moon'
    phaseName = 'Fresh Beginnings'
    description = 'Energy is building and creativity flows. Perfect time to start new projects, brainstorm, and take action on fresh ideas.'
  } else if (cycleDay >= 14 && cycleDay <= 16) {
    menstrualPhase = 'Ovulatory'
    moonPhase = 'Full Moon'
    phaseName = 'Radiant Power'
    description = 'You are at your most magnetic and confident. Ideal for presentations, networking, important conversations, and being seen.'
  } else {
    menstrualPhase = 'Luteal'
    moonPhase = 'Waning Moon'
    phaseName = 'Focused Completion'
    description = 'Time to finish projects, organize, and prepare. Your attention to detail is sharp and you can power through tasks.'
  }

  return {
    id: Math.floor(cycleDay / 7) + 1,
    name: phaseName,
    moonPhase,
    menstrualPhase,
    description,
    cycleDay,
    daysSinceLastPeriod
  }
}

// Calculate current moon phase
export const calculateMoonPhase = () => {
  const today = new Date()
  // Known new moon date (you can update this periodically)
  const knownNewMoon = new Date('2024-01-11') // January 11, 2024 was a new moon
  const daysSinceNewMoon = Math.floor((today - knownNewMoon) / (1000 * 60 * 60 * 24))

  // Lunar cycle is approximately 29.5 days
  const lunarCycle = 29.5
  const currentLunarDay = (daysSinceNewMoon % lunarCycle) + 1

  let moonPhase, phaseName, description

  if (currentLunarDay >= 1 && currentLunarDay <= 7) {
    moonPhase = 'New Moon'
    phaseName = 'Deep Renewal'
    description = 'The moon is in her quiet, introspective phase. Perfect for rest, reflection, setting intentions, and connecting with your inner wisdom.'
  } else if (currentLunarDay >= 8 && currentLunarDay <= 14) {
    moonPhase = 'Waxing Moon'
    phaseName = 'Fresh Beginnings'
    description = 'The moon is growing in light and energy. Ideal for starting new projects, taking action, and building momentum toward your goals.'
  } else if (currentLunarDay >= 15 && currentLunarDay <= 21) {
    moonPhase = 'Full Moon'
    phaseName = 'Radiant Power'
    description = 'The moon is at her peak brightness and power. Perfect for celebrations, important presentations, harvesting results, and being fully visible.'
  } else {
    moonPhase = 'Waning Moon'
    phaseName = 'Focused Completion'
    description = 'The moon is releasing and letting go. Excellent for finishing projects, organizing, decluttering, and preparing for the next cycle.'
  }

  return {
    id: Math.ceil(currentLunarDay / 7),
    name: phaseName,
    moonPhase,
    menstrualPhase: null, // No menstrual tracking
    description,
    lunarDay: Math.floor(currentLunarDay)
  }
}

// Get moon phase emoji
export const getMoonPhaseEmoji = (moonPhase) => {
  const moonEmojis = {
    'New Moon': '🌑',
    'Waxing Moon': '🌒',
    'Full Moon': '🌕',
    'Waning Moon': '🌘'
  }
  return moonEmojis[moonPhase] || '🌙'
}

// Check if user has set up their cycle data
export const hasPersonalizedData = () => {
  return localStorage.getItem('trackingMode') !== null
}

// Get user's current phase or fallback to demo data
export const getCurrentPhase = () => {
  const trackingMode = localStorage.getItem('trackingMode')
  
  console.log('getCurrentPhase - trackingMode:', trackingMode)

  if (trackingMode === 'moon') {
    const moonPhase = calculateMoonPhase()
    console.log('getCurrentPhase - moonPhase:', moonPhase)
    return moonPhase
  } else if (trackingMode === 'cycle') {
    const lastPeriodDate = localStorage.getItem('lastPeriodDate')
    const cycleLength = parseInt(localStorage.getItem('cycleLength')) || 28
    
    console.log('getCurrentPhase - cycle data:', { lastPeriodDate, cycleLength })
    
    if (lastPeriodDate) {
      const cyclePhase = calculateCurrentPhase(lastPeriodDate, cycleLength)
      console.log('getCurrentPhase - cyclePhase:', cyclePhase)
      return cyclePhase
    }
  }

  console.log('getCurrentPhase - returning null (will use demo data)')
  return null // Will use demo data from phases.json
}

// Get tracking mode
export const getTrackingMode = () => {
  return localStorage.getItem('trackingMode') || null
}